import { ECountry, ELegalIdType } from '@constants';
import { DatePicker, Flex, Form, Input } from 'antd';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

import { StepWrapper } from '../../../styles';
import { TranslatedTitle } from '../components';

import {
  DocumentInputWrapper,
  FieldLabel,
  InputsWrapper,
  RequiredFieldNote,
  SectionTitle,
  SubtitleText,
  TitlesWrapper
} from './CompleteProfileFP.styles';

import {
  AddressFieldsSection,
  CountrySelector,
  GenderSelector,
  LegalIdTypeSelector,
  NationalitySelector,
  PhoneInput,
  ProvincesExtendedSelector,
  SecondLastNameField
} from '@/components/Form';
import { useLegalIdValidation } from '@/hooks';
import { useProfile } from '@/store';

export const CompleteProfileFP = () => {
  const { t } = useTranslation('StudentOnboardingPageTranslations');
  const { t: tDefault } = useTranslation('DefaultTranslations');
  const { profile } = useProfile();
  const form = Form.useFormInstance();
  const userInfo = Form.useWatch(['userInfo'], form);
  const formRegionConfig = Form.useWatch('regionConfig', form);
  const region = formRegionConfig ?? profile?.regionConfig;
  const { legalIdRule } = useLegalIdValidation({ form, region });

  const birthCountry = userInfo?.countryBirth;
  const isSpain = birthCountry === ECountry.ES;

  const handleLegalIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const upperValue = e.target.value.toUpperCase();
    form.setFieldValue('legalId', upperValue);
  };

  return (
    <StepWrapper>
      <TitlesWrapper>
        <TranslatedTitle i18nKey="COMPLETE_PROFILE" />
        <SubtitleText>{t('COMPLETE_PROFILE_SUBTITLE')}</SubtitleText>
        <RequiredFieldNote>(*) {t('REQUIRED_FIELDS_NOTE')}</RequiredFieldNote>
      </TitlesWrapper>

      <Flex vertical gap={16}>
        <SectionTitle>{t('GENERAL_INFORMATION')}</SectionTitle>

        <InputsWrapper>
          <FieldLabel>
            <span className="required">*</span> {t('NAME')}
          </FieldLabel>
          <Form.Item
            rules={[
              {
                required: true,
                message: t('REQUIRED_NAME')
              }
            ]}
            name="name">
            <Input placeholder={t('NAME_PLACEHOLDER')} />
          </Form.Item>
        </InputsWrapper>

        <InputsWrapper>
          <FieldLabel>
            <span className="required">*</span> {t('FIRST_SURNAME')}
          </FieldLabel>
          <Form.Item
            rules={[
              {
                required: true,
                message: t('REQUIRED_FIRST_SURNAME')
              }
            ]}
            name="lastName">
            <Input placeholder={t('FIRST_SURNAME_PLACEHOLDER')} />
          </Form.Item>
        </InputsWrapper>

        <InputsWrapper>
          <FieldLabel>
            <span className="required">*</span> {t('SECOND_SURNAME')}
          </FieldLabel>
          <SecondLastNameField placeholderSecondLastName={t('SECOND_SURNAME_PLACEHOLDER')} />
        </InputsWrapper>

        <InputsWrapper>
          <FieldLabel>
            <span className="required">*</span> {t('PHONE')}
          </FieldLabel>
          <Form.Item
            rules={[
              {
                required: true,
                message: t('REQUIRED_PHONE')
              }
            ]}
            name="phone">
            <PhoneInput placeholder={t('PHONE_PLACEHOLDER')} />
          </Form.Item>
        </InputsWrapper>

        <InputsWrapper>
          <FieldLabel>
            <span className="required">*</span> {t('IDENTIFICATION_DOCUMENT')}
          </FieldLabel>
          <DocumentInputWrapper>
            <Form.Item
              name="legalIdType"
              className="type-selector"
              rules={[
                {
                  required: true,
                  message: t('REQUIRED_DOCUMENT_TYPE')
                }
              ]}
              initialValue={profile?.legalIdType || ELegalIdType.DNI_NIF_NIE}>
              <LegalIdTypeSelector region={region} />
            </Form.Item>
            <Form.Item
              name="legalId"
              className="number-input"
              dependencies={['legalIdType']}
              rules={[
                {
                  required: true,
                  message: t('REQUIRED_DOCUMENT_NUMBER')
                },
                legalIdRule
              ]}>
              <Input
                placeholder={t('DOCUMENT_NUMBER_PLACEHOLDER')}
                onChange={handleLegalIdChange}
              />
            </Form.Item>
          </DocumentInputWrapper>
        </InputsWrapper>

        <InputsWrapper>
          <FieldLabel>
            <span className="required">*</span> {t('NATIONALITY')}
          </FieldLabel>
          <Form.Item
            rules={[
              {
                required: true,
                message: t('REQUIRED_NATIONALITY')
              }
            ]}
            name={['userInfo', 'nationality']}>
            <NationalitySelector placeholder={t('SELECT_OPTION')} />
          </Form.Item>
        </InputsWrapper>

        <SectionTitle>{t('BIRTH_DATA')}</SectionTitle>

        <InputsWrapper>
          <FieldLabel>
            <span className="required">*</span> {t('BIRTH_DATE')}
          </FieldLabel>
          <Form.Item
            rules={[
              {
                required: true,
                message: t('REQUIRED_BIRTH_DATE')
              }
            ]}
            name="birthday">
            <DatePicker
              format="DD/MM/YYYY"
              maxDate={dayjs()}
              placeholder={t('SELECT_BIRTH_DATE')}
              style={{ width: '100%' }}
            />
          </Form.Item>
        </InputsWrapper>

        <InputsWrapper>
          <FieldLabel>
            <span className="required">*</span> {t('SEX')}
          </FieldLabel>
          <Form.Item
            rules={[
              {
                required: true,
                message: t('REQUIRED_SEX')
              }
            ]}
            name={['userInfo', 'gender']}>
            <GenderSelector placeholder={t('SELECT_OPTION')} />
          </Form.Item>
        </InputsWrapper>

        <InputsWrapper>
          <FieldLabel>
            <span className="required">*</span> {t('BIRTH_COUNTRY')}
          </FieldLabel>
          <Form.Item
            rules={[
              {
                required: true,
                message: t('REQUIRED_BIRTH_COUNTRY')
              }
            ]}
            name={['userInfo', 'countryBirth']}>
            <CountrySelector disableDescription placeholder={t('SELECT_OPTION')} />
          </Form.Item>
        </InputsWrapper>

        {isSpain && (
          <>
            <InputsWrapper>
              <FieldLabel>
                <span className="required">*</span> {tDefault('PROVINCE')}
              </FieldLabel>
              <Form.Item
                rules={[
                  {
                    required: true,
                    message: tDefault('REQUIRED_PROVINCE')
                  }
                ]}
                name={['userInfo', 'birthProvince']}>
                <ProvincesExtendedSelector />
              </Form.Item>
            </InputsWrapper>

            <InputsWrapper>
              <FieldLabel>
                <span className="required">*</span> {tDefault('MUNICIPALITY')}
              </FieldLabel>
              <Form.Item
                rules={[
                  {
                    required: true,
                    message: tDefault('REQUIRED_MUNICIPALITY')
                  }
                ]}
                name={['userInfo', 'birthMunicipality']}>
                <Input />
              </Form.Item>
            </InputsWrapper>

            <InputsWrapper>
              <FieldLabel>
                <span className="required">*</span> {tDefault('LOCALITY')}
              </FieldLabel>
              <Form.Item
                rules={[
                  {
                    required: true,
                    message: tDefault('REQUIRED_LOCALITY')
                  }
                ]}
                name={['userInfo', 'birthLocality']}>
                <Input />
              </Form.Item>
            </InputsWrapper>
          </>
        )}

        {birthCountry && !isSpain && (
          <InputsWrapper>
            <FieldLabel>
              <span className="required">*</span> {t('FOREIGN_BIRTH_LOCALITY')}
            </FieldLabel>
            <Form.Item
              rules={[
                {
                  required: true,
                  message: t('REQUIRED_FOREIGN_BIRTH_LOCALITY')
                }
              ]}
              name={['userInfo', 'birthLocalityForeign']}>
              <Input placeholder={t('FOREIGN_BIRTH_LOCALITY_PLACEHOLDER')} />
            </Form.Item>
          </InputsWrapper>
        )}

        <SectionTitle>{t('ADDRESS_DATA')}</SectionTitle>

        <AddressFieldsSection form={form} />
      </Flex>
    </StepWrapper>
  );
};
